import { resetStore } from '@/lib/valtio/reset-store'
import type { Rundown } from '@rundown-studio/types'

import { proxy, useSnapshot } from 'valtio'
import { computed } from 'valtio-reactive'
import { deepClone, devtools } from 'valtio/utils'
import type { ModalControls } from '../modal-manager/model/types'
import type UpgradeModal from '@/components/modal/UpgradeModal'

type RundownPageStore = {
  rundown: Rundown
  // cues: ...
  // columns: ...
  /** Legacy modals */
  modals: {
    upgradeModal: ModalControls<typeof UpgradeModal> | null // TODO: Migrate legacy modals
  }
}

const initialState = {
  rundown: {} as Rundown,
  modals: {
    upgradeModal: null,
  },
} as const satisfies RundownPageStore

export const rundownPageStore = proxy<RundownPageStore>(deepClone(initialState))

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(rundownPageStore, { name: 'rundownPageStore', enabled: true })
}
// #endregion

type RundownPageStoreDerived = {
  columnsCount: number
}

export const rundownPageStoreDerived = computed<RundownPageStoreDerived>({
  columnsCount: () => rundownPageStore.rundown?.columns?.length ?? 0,
})

export function initRundownPageStore(rundown: Rundown) {
  // Make a copy to avoid mutated references
  const clonedRundown = structuredClone(rundown)

  rundownPageStore.rundown = clonedRundown
}

export function resetRundownPageStore() {
  return resetStore(rundownPageStore, initialState)
}

// #region ============== HOOKS

export function useRundownPageSnap() {
  return useSnapshot(rundownPageStore)
}

export function useRundownPageDerived() {
  return useSnapshot(rundownPageStoreDerived)
}

/**
 * Convenience hook for accessing snapshot of `rundown` in rundownPageStore
 */
export function useRundownSnap() {
  if (!rundownPageStore.rundown) throw new Error('Rundown not initalized')
  return useSnapshot(rundownPageStore.rundown)
}

// #endregion
